import React, {memo, RefObject, ReactNode, useEffect, useState} from 'react'
import {Box, Button} from '@mui/material'
import {useSelector} from 'react-redux'
import {scrollButtonStyles} from '../../pages/home/stockBar/stockBar'
import {useResizeDetector} from 'react-resize-detector';
import ThinArrowRightIcon from "../../assets/icons/ThinArrowRightIcon";
import ThinArrowLeftIcon from "../../assets/icons/ThinArrowLeftIcon";
import {selectIsTouchEnabled} from '../../selectors/device'

type HorizontalScrollButtonsProps = {
    children: ReactNode
    containerRef: RefObject<HTMLDivElement | undefined>;
    scrollAmount?: number
};

const HorizontalScrollButtons: React.FC<HorizontalScrollButtonsProps> = ({children, containerRef, scrollAmount = 500}) => {
    const {width, ref} = useResizeDetector()
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const isTouchEnabled = useSelector(selectIsTouchEnabled)

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                const {scrollLeft, scrollWidth, clientWidth} = containerRef.current
                setCanScrollLeft(scrollLeft > 0)
                setCanScrollRight(Math.round(scrollLeft) + Math.round(clientWidth) < Math.round(scrollWidth))
            }
        }
        handleScroll()
        const timeoutId = window.setTimeout(handleScroll, 2000)
        const currentRef = containerRef.current
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll)
            return () => {
                window.clearTimeout(timeoutId)
                currentRef.removeEventListener('scroll', handleScroll)
            }
        }
        return () => window.clearTimeout(timeoutId)
    }, [width, children, containerRef])

    const scrollLeft = () => {
        if (containerRef.current) {
            containerRef.current.scrollBy({left: -scrollAmount, behavior: 'smooth'})
        }
    }

    const scrollRight = () => {
        if (containerRef.current) {
            containerRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'})
        }
    }

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            minWidth: 0,
            '&:hover .scroll-buttons': {
                opacity: 1
            }
        }} ref={ref}>
            {!isTouchEnabled && canScrollLeft && (
                <Button
                    className="scroll-buttons"
                    onClick={scrollLeft}
                    sx={{
                        ...scrollButtonStyles,
                        left: 0,
                    }}
                >
                    <ThinArrowLeftIcon fontSize="medium"/>
                </Button>
            )}
            {
                children
            }
            {!isTouchEnabled && canScrollRight && (
                <Button
                    className="scroll-buttons"
                    onClick={scrollRight}
                    sx={{
                        ...scrollButtonStyles,
                        right: 0,
                    }}
                >
                    <ThinArrowRightIcon fontSize={"medium"}/>
                </Button>
            )}
        </Box>
    )
}

export default memo(HorizontalScrollButtons);